import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Popconfirm, Spin, Switch, message } from 'antd';
import { ApiOutlined, CloseOutlined, CloudServerOutlined } from '@ant-design/icons';
import {
  createConnector,
  createMcpServer,
  linkAssistantConnector,
  linkAssistantMcpServer,
  listAssistantConnectors,
  listAssistantMcpServers,
  listConnectors,
  listMcpServers,
  removeConnectorAssistantLink,
  removeMcpServerAssistantLink,
  setConnectorAssistantLink,
  setMcpServerAssistantLink,
} from '../../services/api/agents';
import AddActionDropdown from '../ui/AddActionDropdown';
import AddToolModal from './AddToolModal';
import {
  buildHttpAuth,
  buildMcpAuth,
  handleMcpConnectResult,
  parseParametersSchema,
  DEFAULT_PARAMS_SCHEMA,
} from './toolFormUtils';

export default function AssistantToolsSection({ keycloak, assistantId, isEditMode }) {
  const navigate = useNavigate();
  const [httpLinks, setHttpLinks] = useState([]);
  const [mcpLinks, setMcpLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addType, setAddType] = useState(null);
  const [allHttp, setAllHttp] = useState([]);
  const [allMcp, setAllMcp] = useState([]);

  const load = useCallback(async () => {
    if (!keycloak?.authenticated || !assistantId) return;
    setLoading(true);
    try {
      // Show linked tools as soon as assistant links return; global catalogs are secondary.
      const [httpL, mcpL] = await Promise.all([
        listAssistantConnectors(keycloak, assistantId),
        listAssistantMcpServers(keycloak, assistantId),
      ]);
      setHttpLinks(httpL || []);
      setMcpLinks(mcpL || []);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }

    try {
      const [httpAll, mcpAll] = await Promise.all([
        listConnectors(keycloak),
        listMcpServers(keycloak),
      ]);
      setAllHttp(httpAll || []);
      setAllMcp(mcpAll || []);
    } catch {
      // Hint-only data; linked list already rendered.
    }
  }, [keycloak, assistantId]);

  useEffect(() => {
    if (isEditMode) load();
  }, [isEditMode, load]);

  const rows = useMemo(() => {
    const httpRows = (httpLinks || []).map((l) => ({
      key: `http-${l.connector_id}`,
      type: 'HTTP',
      name: l.name,
      sourceId: l.connector_id,
      rowType: 'http',
      enabled: l.enabled,
    }));
    const mcpRows = (mcpLinks || []).map((l) => ({
      key: `mcp-${l.mcp_server_id}`,
      type: 'MCP',
      name: l.name,
      sourceId: l.mcp_server_id,
      rowType: 'mcp',
      enabled: l.enabled,
    }));
    return [...httpRows, ...mcpRows];
  }, [httpLinks, mcpLinks]);

  const linkedHttpIds = new Set(httpLinks.map((l) => l.connector_id));
  const linkedMcpIds = new Set(mcpLinks.map((l) => l.mcp_server_id));
  const hasUnlinked = allHttp.some((c) => !linkedHttpIds.has(c.id))
    || allMcp.some((s) => !linkedMcpIds.has(s.id));

  const toggleLink = async (row, enabled) => {
    try {
      if (row.rowType === 'http') {
        await setConnectorAssistantLink(keycloak, row.sourceId, assistantId, { enabled });
      } else {
        await setMcpServerAssistantLink(keycloak, row.sourceId, assistantId, { enabled });
      }
      await load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const removeLink = async (row) => {
    try {
      if (row.rowType === 'http') {
        await removeConnectorAssistantLink(keycloak, row.sourceId, assistantId);
      } else {
        await removeMcpServerAssistantLink(keycloak, row.sourceId, assistantId);
      }
      message.success('Removed');
      await load();
    } catch (e) {
      message.error(e.message);
    }
  };

  if (!isEditMode) {
    return (
      <div className="settings-list-row">
        <div className="settings-list-row-main">
          <div className="settings-list-row-name">No tools yet</div>
          <div className="settings-list-row-meta">
            Connect HTTP APIs and MCP servers after this assistant is created.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Spin spinning={loading}>
        {rows.length === 0 ? (
          <p className="settings-note">No tools linked yet.</p>
        ) : (
          <div className="settings-list">
            {rows.map((row) => (
              <div key={row.key} className="settings-list-row">
                <div className="settings-list-row-main">
                  <div className="settings-list-row-name">{row.name}</div>
                  <div className="settings-list-row-meta">{row.type}</div>
                </div>
                <div className="settings-list-actions">
                  <Switch checked={Boolean(row.enabled)} onChange={(value) => toggleLink(row, value)} />
                  <Popconfirm title="Remove from assistant?" onConfirm={() => removeLink(row)}>
                    <Button className="settings-icon-btn" type="text" icon={<CloseOutlined />} aria-label="Remove" />
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>
        )}
      </Spin>
      <div className="settings-tools-actions">
        <AddActionDropdown
          label="Add tool"
          items={[
            {
              key: 'http',
              title: 'HTTP source',
              subtitle: 'OpenAPI spec or a custom REST endpoint.',
              icon: <ApiOutlined />,
              onClick: () => setAddType('http'),
            },
            {
              key: 'mcp',
              title: 'MCP server',
              subtitle: 'Remote Model Context Protocol server.',
              icon: <CloudServerOutlined />,
              onClick: () => setAddType('mcp'),
            },
          ]}
        />
        <button type="button" className="settings-quiet-link" style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer' }} onClick={() => navigate('/connectors')}>
          Manage all tools
        </button>
      </div>
      <AddToolModal
        open={Boolean(addType)}
        type={addType}
        onCancel={() => setAddType(null)}
        onCreateHttp={async (values, createMode) => {
          const payload = {
            name: values.name,
            description: values.description,
            base_url: values.base_url,
            auth: buildHttpAuth(values),
          };
          if (createMode === 'openapi') {
            if (!values.openapi_url && !values.openapi_spec_text) {
              throw new Error('Provide an OpenAPI URL or paste a JSON spec');
            }
            if (values.openapi_spec_text) payload.openapi_spec = JSON.parse(values.openapi_spec_text);
            else if (values.openapi_url) payload.openapi_url = values.openapi_url;
          } else {
            payload.initial_tool = {
              name: values.tool_name,
              description: values.tool_description || values.tool_name,
              http_method: values.http_method || 'GET',
              path_template: values.path_template,
              parameters_schema: parseParametersSchema(values.parameters_schema_text || DEFAULT_PARAMS_SCHEMA),
              kind: values.kind || 'read',
            };
          }
          const created = await createConnector(keycloak, payload);
          await linkAssistantConnector(keycloak, assistantId, { connector_id: created.id, enabled: true });
          message.success('HTTP source linked');
          setAddType(null);
          await load();
        }}
        onCreateMcp={async (values) => {
          const result = await createMcpServer(keycloak, {
            name: values.name,
            url: values.url,
            auth: buildMcpAuth(values),
          });
          await linkAssistantMcpServer(keycloak, assistantId, { mcp_server_id: result.id, enabled: true });
          handleMcpConnectResult(result, {});
          message.success('MCP server linked');
          setAddType(null);
          await load();
        }}
      />
      {hasUnlinked && !addType ? (
        <p className="settings-hint" style={{ marginTop: 8 }}>
          You have existing tools not linked to this assistant. Open Manage all tools to link them.
        </p>
      ) : null}
    </>
  );
}
